export type AiAutoResumeTrigger =
  | 'live-transition'
  | 'history-selection'
  | 'claim-resume'
  | 'hydration'

export type AiAutoResumeAction = 'check_info' | 'generate_document'

export interface AiAutoResumeRequest {
  action: AiAutoResumeAction
  stream: boolean
}

const AUTO_RESUME_REQUESTS: Readonly<Record<AiAutoResumeAction, AiAutoResumeRequest>> = {
  check_info: {
    action: 'check_info',
    stream: false,
  },
  generate_document: {
    action: 'generate_document',
    stream: true,
  },
}

const AUTO_RESUME_TRIGGERS: ReadonlySet<AiAutoResumeTrigger> = new Set([
  'live-transition',
  'history-selection',
  'claim-resume',
])

const AI_HISTORY_SELECTION_STATE_KEY = 'aiConversationHistorySelection'

interface AiHistorySelectionState {
  [AI_HISTORY_SELECTION_STATE_KEY]: {
    conversationId: string
  }
}

export function resolveAiAutoResumeRequest(
  step: string | undefined,
  trigger: AiAutoResumeTrigger,
): AiAutoResumeRequest | null {
  if (!step || !AUTO_RESUME_TRIGGERS.has(trigger)) return null
  return AUTO_RESUME_REQUESTS[step as AiAutoResumeAction] ?? null
}

export function createAiHistorySelectionState(conversationId: string): AiHistorySelectionState {
  return {
    [AI_HISTORY_SELECTION_STATE_KEY]: {
      conversationId,
    },
  }
}

export function readAiHistorySelectionConversationId(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null
  const selection = (state as Record<string, unknown>)[AI_HISTORY_SELECTION_STATE_KEY]
  if (!selection || typeof selection !== 'object') return null
  const conversationId = (selection as Record<string, unknown>).conversationId
  return typeof conversationId === 'string' && conversationId.trim() ? conversationId : null
}

export function buildAiAutoResumeKey(
  conversationId: string,
  lastMessageId: string,
  step: string | undefined,
): string | null {
  const normalizedConversationId = conversationId.trim()
  const normalizedLastMessageId = lastMessageId.trim()
  const normalizedStep = step?.trim()
  if (!normalizedConversationId || !normalizedLastMessageId || !normalizedStep) return null
  return JSON.stringify([normalizedConversationId, normalizedLastMessageId, normalizedStep])
}
